import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readFensteransicht } from "@/office/excel/analyse";
import { mitErsatz, pruefeMitMakro } from "@/office/makro/pruefung";
import { EXCEL_ANSICHT, MAKRO_ANSICHT } from "../makros";

const NAMEN: Record<string, string> = {
  normalView: "Normal",
  pageBreakPreview: "Umbruchvorschau",
  pageLayoutView: "Seitenlayout",
};

export const lektionAnsichten: Lesson = {
  id: "ansichten",
  title: "Ansichten und Fenster",
  summary: "Normal, Umbruchvorschau, Seitenlayout – und zwei Stellen einer Mappe gleichzeitig im Blick.",
  durationMinutes: 9,
  learningGoals: [
    "Zwischen Normal, Umbruchvorschau und Seitenlayout wechseln",
    "Ein Fenster teilen oder ein zweites Fenster derselben Mappe öffnen",
  ],
  steps: [
    {
      id: "umbruchvorschau",
      title: "Die drei Arbeitsansichten",
      explanation: [
        "Unten rechts in der Statusleiste und auf der Registerkarte „Ansicht“ stehen drei " +
          "Ansichten. „Normal“ ist die Arbeitsansicht. „Seitenlayout“ zeigt Seiten mit " +
          "Rändern, Kopf- und Fußzeile – dort lässt sich direkt in die Kopfzeile klicken. " +
          "„Umbruchvorschau“ zeigt grau, was nicht gedruckt wird, und blau die Seitengrenzen.",
        "Vor jedem Ausdruck lohnt ein Blick in die Umbruchvorschau: Dort sieht man sofort, " +
          "ob eine einzelne Spalte auf eine eigene Seite rutscht.",
      ],
      keyPoints: [
        "Ansicht → Normal / Umbruchvorschau / Seitenlayout.",
        "Die Schaltflächen stehen auch unten rechts in der Statusleiste.",
        "Umbruchvorschau vor jedem Druck.",
      ],
      task: {
        instruction: "Wechsle für dein Blatt in die Umbruchvorschau.",
        hint: "Ansicht → Umbruchvorschau, oder die mittlere der drei Schaltflächen unten rechts.",
        macro: MAKRO_ANSICHT,
        macroNurErsatz: true,
        check: () =>
          mitErsatz(
            readFensteransicht,
            MAKRO_ANSICHT,
            (bericht) => EXCEL_ANSICHT[bericht.zahl("ansicht") ?? 1] ?? "normalView",
            (ansicht) =>
              fromCriteria(
                [criterion(`Umbruchvorschau ist aktiv (aktuell: ${NAMEN[ansicht] ?? ansicht})`, ansicht === "pageBreakPreview")],
                {
                  success: "Jetzt siehst du, was auf welcher Seite landet.",
                  failure: "Die Umbruchvorschau ist nicht aktiv.",
                },
              ),
          ),
      },
    },
    {
      id: "fenster",
      title: "Teilen und Neues Fenster",
      explanation: [
        "„Ansicht“ → „Teilen“ zerlegt das Fenster in bis zu vier Bereiche, die unabhängig " +
          "scrollen – oben die Eingaben, unten Zeile 800. Anders als „Fenster fixieren“ " +
          "(Modul „Grundlagen“) bleibt dabei nichts stehen; jeder Bereich zeigt einen " +
          "beliebigen Ausschnitt.",
        "„Ansicht“ → „Neues Fenster“ öffnet dieselbe Mappe ein zweites Mal – in der " +
          "Titelleiste steht dann „:1“ und „:2“. Mit „Alle anordnen“ stehen beide " +
          "nebeneinander, etwa Blatt „Daten“ links und Blatt „Auswertung“ rechts.",
      ],
      keyPoints: [
        "Ansicht → Teilen: vier unabhängig scrollende Bereiche.",
        "Ansicht → Neues Fenster + Alle anordnen: zwei Blätter nebeneinander.",
      ],
      task: {
        instruction: "Teile das Fenster oder öffne ein zweites Fenster derselben Arbeitsmappe.",
        hint: "Ansicht → Teilen, oder Ansicht → Neues Fenster.",
        macro: MAKRO_ANSICHT,
        check: () =>
          pruefeMitMakro(MAKRO_ANSICHT, (bericht) => {
            const geteilt = bericht.janein("geteilt") === true;
            const fenster = bericht.zahl("fenster") ?? 1;
            return fromCriteria(
              [criterion(`Fenster geteilt oder mehrere Fenster geöffnet (Fenster: ${fenster})`, geteilt || fenster > 1)],
              {
                success: "Zwei Stellen der Mappe gleichzeitig im Blick.",
                failure: "Das Fenster ist weder geteilt noch doppelt geöffnet.",
                hint: "Das Makro muss laufen, während die Teilung oder das zweite Fenster noch besteht.",
              },
            );
          }),
      },
    },
  ],
};
